import type { SupabaseClient } from '@supabase/supabase-js';

export interface CollectionCore {
    id: string;
    user_id: string;
    name: string;
    color: string;
    description?: string | null;
    message_context?: string | null;
    image_url?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface CollectionMemberCore {
    collection_id: string;
    contact_id: string;
    added_by?: string | null;
    created_at?: string;
}

// Collections
export async function listCollectionsCore(sb: SupabaseClient, userId: string): Promise<CollectionCore[]> {
    const { data, error } = await sb
        .from('contact_collections')
        .select('*')
        .eq('user_id', userId)
        .order('name');
    if (error) throw error;
    return (data || []) as CollectionCore[];
}

export interface CollectionWithSharingCore extends CollectionCore {
    member_count?: number;
    is_shared?: boolean;
    shared_by?: string;
    shares?: any[];
}

export async function listCollectionsWithSharingCore(sb: SupabaseClient, userId: string, userEmail: string): Promise<CollectionWithSharingCore[]> {
    // Get owned collections with member counts and shares
    const { data: ownedCollections, error: ownedError } = await sb
        .from('contact_collections')
        .select(`
            *,
            member_count:contact_collection_members(count),
            shares:collection_shares(*)
        `)
        .eq('user_id', userId)
        .order('name');

    if (ownedError) throw ownedError;

    // Get shared collections (accepted shares)
    const userEmailLower = (userEmail || '').toLowerCase();
    const { data: sharedCollections, error: sharedError } = await sb
        .from('collection_shares')
        .select(`
            collection:contact_collections(*),
            shared_by_user_id
        `)
        .eq('status', 'accepted')
        .or(`shared_to_email.eq.${userEmailLower},shared_to_user_id.eq.${userId}`);

    if (sharedError) throw sharedError;

    // Combine owned and shared collections
    const collections: CollectionWithSharingCore[] = [];

    // Add owned collections
    for (const collection of ownedCollections || []) {
        collections.push({
            ...collection,
            member_count: collection.member_count?.[0]?.count || 0,
            is_shared: false,
            shares: collection.shares || []
        });
    }

    // Add shared collections
    for (const share of sharedCollections || []) {
        if (share.collection) {
            // Get member count for shared collection
            const { data: memberCount } = await sb
                .from('contact_collection_members')
                .select('id', { count: 'exact' })
                .eq('collection_id', share.collection.id);

            collections.push({
                ...share.collection,
                member_count: memberCount?.length || 0,
                is_shared: true,
                shared_by: share.shared_by_user_id,
                shares: []
            });
        }
    }

    // Sort by name
    collections.sort((a, b) => a.name.localeCompare(b.name));

    return collections;
}

export async function getCollectionCore(sb: SupabaseClient, id: string): Promise<CollectionCore | null> {
    const { data, error } = await sb
        .from('contact_collections')
        .select('id, user_id, name, color, description, message_context, image_url, created_at, updated_at')
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    return (data as CollectionCore) || null;
}

export async function createCollectionCore(sb: SupabaseClient, userId: string, input: { name: string; color?: string; description?: string | null; message_context?: string | null; image_url?: string | null }): Promise<CollectionCore> {
    const { data, error } = await sb
        .from('contact_collections')
        .insert({
            user_id: userId,
            name: input.name.trim(),
            color: input.color || '#3B82F6',
            description: input.description ?? null,
            message_context: input.message_context ?? null,
            image_url: input.image_url ?? null,
            updated_at: new Date().toISOString(),
        })
        .select('*')
        .single();
    if (error) throw error;
    return data as CollectionCore;
}

export async function updateCollectionCore(sb: SupabaseClient, id: string, patch: Partial<Pick<CollectionCore, 'name' | 'color' | 'description' | 'message_context' | 'image_url'>>): Promise<CollectionCore> {
    const { data, error } = await sb
        .from('contact_collections')
        .update({
            ...(patch.name !== undefined ? { name: patch.name.trim() } : {}),
            ...(patch.color !== undefined ? { color: patch.color } : {}),
            ...(patch.description !== undefined ? { description: patch.description } : {}),
            ...(patch.message_context !== undefined ? { message_context: patch.message_context } : {}),
            ...(patch.image_url !== undefined ? { image_url: patch.image_url } : {}),
            updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw error;
    return data as CollectionCore;
}

export async function deleteCollectionCore(sb: SupabaseClient, id: string): Promise<void> {
    const { error } = await sb
        .from('contact_collections')
        .delete()
        .eq('id', id);
    if (error) throw error;
}

// Memberships
export async function listMembershipsForCollectionsCore(sb: SupabaseClient, collectionIds: string[], contactIds?: string[]): Promise<CollectionMemberCore[]> {
    if (!Array.isArray(collectionIds) || collectionIds.length === 0) return [];
    let q = sb
        .from('contact_collection_members')
        .select('collection_id, contact_id');
    q = q.in('collection_id', collectionIds);
    if (contactIds && contactIds.length > 0) q = q.in('contact_id', contactIds);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as CollectionMemberCore[];
}

export async function bulkAddMembersCore(sb: SupabaseClient, ops: Array<{ collection_id: string; contact_id: string; added_by?: string }>): Promise<number> {
    if (!ops || ops.length === 0) return 0;
    const { error } = await sb
        .from('contact_collection_members')
        .insert(ops);
    if (error) throw error;
    return ops.length;
}

export async function bulkRemoveMembersCore(sb: SupabaseClient, map: Record<string, string[]>): Promise<number> {
    let removed = 0;
    const cids = Object.keys(map || {});
    for (const cid of cids) {
        const ids = (map[cid] || []).filter(Boolean);
        if (ids.length === 0) continue;
        const { error } = await sb
            .from('contact_collection_members')
            .delete()
            .eq('collection_id', cid)
            .in('contact_id', ids);
        if (error) throw error;
        removed += ids.length;
    }
    return removed;
}

export async function removeContactsFromCollectionsCore(sb: SupabaseClient, collectionIds: string[], contactIds: string[]): Promise<void> {
    if (!collectionIds.length || !contactIds.length) return;
    const { error } = await sb
        .from('contact_collection_members')
        .delete()
        .in('collection_id', collectionIds)
        .in('contact_id', contactIds);
    if (error) throw error;
}

export interface CollectionMemberWithContactCore extends CollectionMemberCore {
    id?: string;
    added_at?: string;
    contact?: {
        id: string;
        first_name: string;
        last_name: string;
        company?: string | null;
        profile_image_url?: string | null;
        tier?: string;
        email?: string | null;
        phone?: string | null;
        is_priority?: boolean;
    };
}

export async function listCollectionMembersWithContactCore(sb: SupabaseClient, collectionId: string): Promise<CollectionMemberWithContactCore[]> {
    const { data, error } = await sb
        .from('contact_collection_members')
        .select(`
            id,
            collection_id,
            contact_id,
            added_by,
            added_at,
            contact:contact_id (
                id,
                first_name,
                last_name,
                company,
                profile_image_url,
                tier,
                email,
                phone,
                is_priority
            )
        `)
        .eq('collection_id', collectionId)
        .order('added_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((item: any) => ({
        ...item,
        contact: Array.isArray(item.contact) ? item.contact[0] : item.contact
    })) as CollectionMemberWithContactCore[];
}

export async function removeCollectionMemberByIdCore(sb: SupabaseClient, memberId: string): Promise<void> {
    const { error } = await sb
        .from('contact_collection_members')
        .delete()
        .eq('id', memberId);
    if (error) throw error;
}
